import React, { useEffect, useState } from 'react'
import MainModal from 'web/src/components/modals/common/MainModal'
import { MESSAGES_CONST } from '../../const/messages-const'
import { MODAL_CONSTS } from '../../const/modal-const'
import { formatDateWithUnderscores } from '../../helpers/time'
import useToasterHelper from '../../helpers/ToasterHelper'
import {
  CUSTOM_DATA_EXPORT_OPTIONS,
  ExportOptionItem,
} from '../../pages/organizer/manage/tabs/reports/components/EventResultsReport/custom-data-export-const'
import {
  ExportData,
  exportDocument,
} from '../../pages/organizer/manage/tabs/reports/components/EventResultsReport/customDataExportService'
import ExportOption from '../../pages/organizer/manage/tabs/reports/components/EventResultsReport/ExportOption'

type CustomDataExportModalProps = {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  dataToPassOn: ExportData
}

const CustomDataExportModal: React.FC<CustomDataExportModalProps> = ({
  show,
  handleModal,
  dataToPassOn,
}) => {
  const toast = useToasterHelper()
  const [isExporting, setIsExporting] = useState(false)
  const [hasSelectedOption, setHasSelectedOption] = useState(false)

  type EnabledOptionsKeys = keyof typeof enabledOptions

  const [enabledOptions, setEnabledOptions] = useState({
    eventReport: false,
    classReport: false,
    scoreReport: false,
    scheduleReport: false,
    stallReport: false,
    feesReport: false,
    personnelReport: false,
    teamReport: false,
    questionsReport: false,
    horseReport: false,
    riderReport: false,
    trainerReport: false,
    ownerReport: false,
    guardianReport: false,
    judgeReport: false,
    staffReport: false,
    sponsorReport: false,
    onsiteVendorReport: false,
    virtualVendorReport: false,
  })

  useEffect(() => {
    const anyOptionSelected = Object.values(enabledOptions).some((value) => value === true)
    setHasSelectedOption(anyOptionSelected)
  }, [enabledOptions])

  const handleToggleOption = (id: ExportOptionItem['id'], enabled: boolean) => {
    setEnabledOptions((prev) => ({
      ...prev,
      [id]: enabled,
    }))
  }

  const closeModal = () => {
    handleModal(false, MODAL_CONSTS.CUSTOM_DATA_EXPORT)
  }

  const onExport = async () => {
    try {
      // check if at least one option is selected
      const hasSelectedOption = Object.values(enabledOptions).some((value) => value === true)

      if (!hasSelectedOption) {
        return
      }

      setIsExporting(true)

      const eventName = dataToPassOn.event?.eventName || 'Event'
      const eventDate = formatDateWithUnderscores(
        dataToPassOn.eventReviewPublish?.EventDetails?.competitionStartDate,
        'event'
      )
      const fileName = `${eventDate}_${eventName}_data_export.xlsx`

      try {
        await exportDocument(dataToPassOn, enabledOptions, fileName)
        toast.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } catch (exportError) {
        console.error('Export processing error: ', exportError)
        toast.error({
          message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      } finally {
        setIsExporting(false)
      }
    } catch (error) {
      console.error('Export error:', error)
      toast.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      closeModal()
    }
  }

  const renderContent = () => {
    return (
      <div>
        <p className="font-normal">
          Each report will export into a separate tab inside the spread sheet.
        </p>

        <div className="my-4 border-[1.5px] border-SeabiscuitLightThemeColorD3 rounded-xl px-4 py-2">
          {CUSTOM_DATA_EXPORT_OPTIONS.map((option) => (
            <ExportOption
              key={option.id}
              iconSrc={option.iconSrc}
              iconAlt={option.iconAlt}
              title={option.title}
              description={option.description}
              enabled={enabledOptions[option.id as EnabledOptionsKeys]}
              onToggle={() =>
                handleToggleOption(
                  option.id as EnabledOptionsKeys,
                  !enabledOptions[option.id as EnabledOptionsKeys]
                )
              }
            />
          ))}
        </div>
      </div>
    )
  }

  const buttons = [
    {
      label: 'Export',
      onClick: onExport,
      textClass: 'text-white text-lg uppercase',
      disabled: !hasSelectedOption,
      loading: isExporting,
      fullWidth: true,
    },
    {
      label: 'Close',
      onClick: closeModal,
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-600 text-lg uppercase',
      borderClass: 'border-transparent',
      fullWidth: true,
    },
  ]

  return (
    <MainModal
      title={'Export Event Data'}
      show={show}
      type="CUSTOM_DATA_EXPORT"
      titleClassName="ml-2 font-semibold "
      onHide={closeModal}
      setHeightAsPerContent
      size="md"
      buttons={buttons}
    >
      <div className="text-SeabiscuitDark200ThemeColor text-base">{renderContent()}</div>
    </MainModal>
  )
}

export default CustomDataExportModal
